import { useCallback, useRef, useState, useContext, useEffect } from 'react';
import { isMobileAny } from '../../utils/device';
import { FocusTrapContext } from './const';

export const useFocusableElements = (
	selector?: string,
): { getFocusableElements: (parentElement: HTMLElement) => NodeListOf<HTMLElement> | undefined } => {
	const getFocusableElements = useCallback(
		(parentElement: HTMLElement) => {
			if (parentElement) {
				const elementSelector = selector
					? selector
					: 'button:not([tabindex="-1"]), input:not([type="hidden"]):not([tabindex="-1"]), select, textarea, [tabindex="0"]';

				return parentElement.querySelectorAll(elementSelector) as NodeListOf<HTMLElement>;
			}
		},
		[selector],
	);

	return {
		getFocusableElements,
	};
};

export const useFocusTrap = ({ hasAutoFocus = true }: { hasAutoFocus: boolean }) => {
	const firstFocusableRef = useRef<HTMLElement | null>(null);
	const lastFocusableRef = useRef<HTMLElement | null>(null);
	const mainContainerRef = useRef<HTMLDivElement | null>(null);
	const [isFocusTrapEnable, setIsFocusTrapEnable] = useState(false);

	const { getFocusableElements } = useFocusableElements();

	const disableFocusTrap = useCallback(() => {
		setIsFocusTrapEnable(false);
		firstFocusableRef.current = null;
		lastFocusableRef.current = null;
	}, []);

	const initFirstLastContentFocusableElements = useCallback(
		(
			focusableElements: NodeListOf<HTMLElement>,
		): Promise<{ firstElement: HTMLElement; lastElement: HTMLElement }> => {
			firstFocusableRef.current = focusableElements[0];
			lastFocusableRef.current = focusableElements[focusableElements.length - 1];

			return Promise.resolve({
				firstElement: firstFocusableRef.current,
				lastElement: lastFocusableRef.current,
			});
		},
		[],
	);

	const initialize = useCallback(
		(focusElements: NodeListOf<HTMLElement>, isAutoFocus: boolean) => {
			initFirstLastContentFocusableElements(focusElements).then((elements) => {
				if (isAutoFocus && elements.firstElement) {
					elements.firstElement.focus();
				}
			});
		},
		[initFirstLastContentFocusableElements],
	);

	const reInitFocusTrap = useCallback(() => {
		if (mainContainerRef.current) {
			setIsFocusTrapEnable(true);

			const focusableElements = getFocusableElements(mainContainerRef.current);

			if (focusableElements) {
				const isFirsElementChanged = focusableElements[0] !== firstFocusableRef.current && hasAutoFocus;

				initialize(focusableElements, isFirsElementChanged);
			}
		}
	}, [initialize, hasAutoFocus, getFocusableElements]);

	const setupFocusTrap = useCallback(
		(container: HTMLDivElement | null, initialFocusTrapStatus) => {
			setIsFocusTrapEnable(initialFocusTrapStatus);
			mainContainerRef.current = container;

			if (initialFocusTrapStatus) {
				reInitFocusTrap();
			} else {
				disableFocusTrap();
			}
		},
		[reInitFocusTrap, disableFocusTrap],
	);

	return {
		disableFocusTrap,
		isFocusTrapEnable,
		reInitFocusTrap,
		setupFocusTrap,
		firstFocusableRef,
		lastFocusableRef,
	};
};

export const useFocusTrapAPI = () => {
	const context = useContext(FocusTrapContext);

	if (context === undefined) {
		throw new Error('useFocusTrapAPI must be used within a FocusTrapProvider');
	}

	return context;
};

export const useFocusTrapDetailPage = ({ isEnable }: { isEnable: boolean }) => {
	const { reInitFocusTrap, disableFocusTrap } = useFocusTrapAPI();

	useEffect(() => {
		if (isEnable && !isMobileAny) {
			reInitFocusTrap();
		}

		return () => {
			if (isEnable && !isMobileAny) {
				disableFocusTrap();
			}
		};
	}, [isEnable, reInitFocusTrap, disableFocusTrap]);
};
